import { create } from 'zustand'
import { driveService } from '../services/googleDrive'
import { AIService, validateApiKey } from '../services/ai'
import {
  checkCryptoSupport,
  deriveEncryptionKey,
  encryptApiKey,
  decryptApiKey,
  generateKeyFingerprint,
  generateSalt,
  saltToBase64,
  base64ToSalt,
} from '../utils/crypto'
import type { AIProvider, AIProviderSettings, EncryptionSalt } from '../types'

const SALT_FILE = 'encryption-salt.json'
const AI_SETTINGS_FILE = 'ai-provider.json'

interface AIState {
  // State
  isInitialised: boolean
  isSupported: boolean
  isLoading: boolean
  isSaving: boolean
  isValidating: boolean
  error: string | null
  
  // Current settings (from Drive)
  provider: AIProvider | null
  keyFingerprint: string | null
  
  // In-memory only (never persisted)
  service: AIService | null
  encryptionKey: CryptoKey | null
  
  // Session flags
  wasKeyReset: boolean
  
  // Actions
  initialise: (googleUserId: string) => Promise<void>
  saveApiKey: (provider: AIProvider, apiKey: string) => Promise<{ success: boolean; warning?: string; error?: string }>
  validateKey: (provider: AIProvider, apiKey: string) => Promise<{ valid: boolean; warning?: string; error?: string }>
  clearApiKey: () => Promise<void>
  clearError: () => void
  getService: () => AIService | null
  isConfigured: () => boolean
}

export const useAIStore = create<AIState>((set, get) => ({
  // Initial state
  isInitialised: false,
  isSupported: false,
  isLoading: false,
  isSaving: false,
  isValidating: false,
  error: null,
  provider: null,
  keyFingerprint: null,
  service: null,
  encryptionKey: null,
  wasKeyReset: false,

  initialise: async (googleUserId: string) => {
    // Check browser support first
    if (!checkCryptoSupport()) {
      set({ isInitialised: true, isSupported: false })
      return
    }

    set({ isLoading: true, error: null, isSupported: true })

    try {
      // Check for account change
      const previousUserId = sessionStorage.getItem('praxis-user-id')
      if (previousUserId && previousUserId !== googleUserId) {
        console.log('[ SYSTEM: GOOGLE ACCOUNT CHANGED. REINITIALISING AI... ]')
        set({ service: null, encryptionKey: null, provider: null, keyFingerprint: null })
      }
      sessionStorage.setItem('praxis-user-id', googleUserId)

      // Get or create salt
      const salt = await getOrCreateSalt()
      
      // Derive encryption key
      const encryptionKey = await deriveEncryptionKey(googleUserId, salt)
      set({ encryptionKey })

      // Load existing settings
      const settings = await driveService.readSettings<AIProviderSettings>(AI_SETTINGS_FILE)

      if (settings?.encryptedKey) {
        try {
          const apiKey = await decryptApiKey(settings.encryptedKey, encryptionKey)
          const service = new AIService(settings.provider, apiKey)
          
          set({
            provider: settings.provider,
            keyFingerprint: settings.keyFingerprint,
            service,
          })
          
          console.log('[ SYSTEM: AI PROVIDER CONFIGURED ]')
        } catch (decryptError) {
          console.error('[ SYSTEM: FAILED TO DECRYPT API KEY ]', decryptError)
          await handleDecryptionFailure(set)
        }
      }

      // Check for previous key reset notification
      const wasReset = sessionStorage.getItem('ai-key-reset') === 'true'
      if (wasReset) {
        sessionStorage.removeItem('ai-key-reset')
        set({ wasKeyReset: true })
      }

      set({ isInitialised: true, isLoading: false })
    } catch (error) {
      console.error('[ SYSTEM: AI INITIALISATION FAILED ]', error)
      set({
        isInitialised: true,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to initialise AI',
      })
    }
  },

  saveApiKey: async (provider: AIProvider, apiKey: string) => {
    const { encryptionKey } = get()
    
    if (!encryptionKey) {
      return { success: false, error: 'Encryption not initialised' }
    }

    set({ isSaving: true, error: null })

    try {
      // Encrypt the key
      const encryptedKey = await encryptApiKey(apiKey, encryptionKey)
      const keyFingerprint = await generateKeyFingerprint(apiKey)

      // Save to Drive
      const settings: AIProviderSettings = {
        provider,
        encryptedKey,
        keyFingerprint,
        updatedAt: new Date().toISOString(),
      }

      await driveService.writeSettings(AI_SETTINGS_FILE, settings)

      // Update in-memory service
      const service = new AIService(provider, apiKey)

      set({
        provider,
        keyFingerprint,
        service,
        isSaving: false,
        wasKeyReset: false,
      })

      console.log('[ SYSTEM: AI PROVIDER SETTINGS SAVED ]')
      return { success: true }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save API key'
      set({ isSaving: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  },

  validateKey: async (provider: AIProvider, apiKey: string) => {
    set({ isValidating: true, error: null })

    try {
      const result = await validateApiKey(provider, apiKey)
      set({ isValidating: false })
      return result
    } catch (error) {
      set({ isValidating: false })
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Validation failed',
      }
    }
  },

  clearApiKey: async () => {
    set({ isSaving: true, error: null })

    try {
      await driveService.deleteSettings(AI_SETTINGS_FILE)
      
      set({
        provider: null,
        keyFingerprint: null,
        service: null,
        isSaving: false,
      })

      console.log('[ SYSTEM: AI PROVIDER SETTINGS CLEARED ]')
    } catch (error) {
      set({
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to clear API key',
      })
    }
  },

  clearError: () => set({ error: null, wasKeyReset: false }),

  getService: () => get().service,

  isConfigured: () => get().service !== null,
}))

// Helper functions

async function getOrCreateSalt(): Promise<Uint8Array> {
  const existing = await driveService.readSettings<EncryptionSalt>(SALT_FILE)

  if (existing?.salt) {
    return base64ToSalt(existing.salt)
  }

  // Generate new random salt
  const salt = generateSalt()
  
  await driveService.writeSettings<EncryptionSalt>(SALT_FILE, {
    salt: saltToBase64(salt),
    createdAt: new Date().toISOString(),
  })

  console.log('[ SYSTEM: ENCRYPTION SALT CREATED ]')
  return salt
}

async function handleDecryptionFailure(
  set: (state: Partial<AIState>) => void
): Promise<void> {
  // Clear corrupted settings (keep salt for potential future use)
  await driveService.deleteSettings(AI_SETTINGS_FILE)

  set({
    provider: null,
    keyFingerprint: null,
    service: null,
  })

  // Set session flag to notify user
  sessionStorage.setItem('ai-key-reset', 'true')
  
  console.log('[ SYSTEM: CORRUPTED AI SETTINGS CLEARED ]')
}

// Convenience function for getting the AI service outside of React
export function getAIService(): AIService | null {
  return useAIStore.getState().service
}

export function isAIConfigured(): boolean {
  return useAIStore.getState().service !== null
}
